import { useEffect, useMemo, useState } from "react";
import { Link, useSearch, useNavigate } from "@tanstack/react-router";
import { Sparkles, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ObjectivesListShell } from "@/features/objectives/objectives-list-shell";
import { CreateObjectiveDialog } from "@/features/objectives/create-objective-dialog";
import { useObjectives, type ObjectiveFilters } from "@/hooks/use-objectives";
import { useFocusAreas } from "@/hooks/use-focus-areas";
import { useTactics } from "@/hooks/use-tactics";
import { useCollegeGoals } from "@/hooks/use-college-goals";
import { useCollegeStrategicPillars } from "@/hooks/use-college-strategic-pillars";
import { useCurrentUser } from "@/hooks/use-current-user";
import { DEFAULT_YEAR } from "@/lib/year-options";
import { buildObjectiveLadder, type ObjectiveLadder } from "@/lib/objective-ladder";
import type { ObjectivesView } from "@/router";

type PrintSearch = Pick<
  ObjectiveFilters,
  "year" | "level" | "status" | "type" | "focus_area_id" | "owner_id"
>;

function buildPrintQueryString(search: PrintSearch): string {
  const params = new URLSearchParams();
  if (search.year != null) params.set("year", String(search.year));
  if (search.level) params.set("level", search.level);
  if (search.status) params.set("status", search.status);
  if (search.type) params.set("type", search.type);
  if (search.focus_area_id) params.set("focus_area_id", search.focus_area_id);
  if (search.owner_id) params.set("owner_id", search.owner_id);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function ObjectivesPage() {
  const { currentUser } = useCurrentUser();
  const search = useSearch({ from: '/objectives' });
  const navigate = useNavigate();
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const view: ObjectivesView = search.view ?? 'cards';

  // Seed URL with defaults on first load so the filter bar reflects what's actually queried.
  useEffect(() => {
    if (search.year === undefined || search.level === undefined) {
      void navigate({
        to: '/objectives',
        search: { ...search, year: search.year ?? DEFAULT_YEAR, level: search.level ?? 'individual' },
        replace: true,
      });
    }
  }, []);

  const filtersWithOwner: ObjectiveFilters = currentUser
    ? {
        year: search.year,
        level: search.level,
        status: search.status,
        type: search.type,
        focus_area_id: search.focus_area_id,
        owner_id: search.owner_id ?? currentUser.id,
        cursor,
      }
    : {};

  const { data, isLoading, error } = useObjectives(filtersWithOwner);
  const { data: focusAreasData } = useFocusAreas();
  const { data: tacticsData } = useTactics();
  const { data: collegeGoalsData } = useCollegeGoals();
  const { data: pillarsData } = useCollegeStrategicPillars();

  const objectives = data?.data ?? [];
  const nextCursor = data?.next_cursor ?? null;

  const ladderData = useMemo(() => ({
    focusAreas: focusAreasData?.data ?? [],
    tactics: tacticsData?.data ?? [],
    collegeGoals: collegeGoalsData?.data ?? [],
    pillars: pillarsData?.data ?? [],
  }), [focusAreasData, tacticsData, collegeGoalsData, pillarsData]);

  const ladderMap = useMemo(() => {
    const map = new Map<string, ObjectiveLadder | null>();
    for (const obj of objectives) {
      map.set(obj.id, buildObjectiveLadder(obj.focus_area_ids, obj.tactic_ids, ladderData));
    }
    return map;
  }, [objectives, ladderData]);

  const handleFilterChange = (newFilters: ObjectiveFilters) => {
    setCursor(undefined);
    void navigate({
      to: '/objectives',
      search: {
        year: newFilters.year,
        level: newFilters.level,
        status: newFilters.status,
        type: newFilters.type,
        focus_area_id: newFilters.focus_area_id,
        owner_id: newFilters.owner_id,
        view: search.view,
      },
    });
  };

  const handleViewChange = (next: ObjectivesView) => {
    void navigate({
      to: '/objectives',
      search: {
        ...search,
        view: next === 'cards' ? undefined : next,
      },
    });
  };

  return (
    <ObjectivesListShell
      headerTitle="My Objectives"
      headerActions={
        <>
          <Button asChild variant="outline">
            <a
              href={`/objectives/print${buildPrintQueryString(search)}`}
              target="_blank"
              rel="noopener"
            >
              <Printer className="mr-2 h-4 w-4" />
              Print
            </a>
          </Button>
          <Button asChild variant="outline">
            <Link to="/objectives/guided-builder">
              <Sparkles className="mr-2 h-4 w-4" />
              Guided OKR Builder
            </Link>
          </Button>
          <CreateObjectiveDialog />
        </>
      }
      showSubtitle
      showSummary
      filters={search}
      onFilterChange={handleFilterChange}
      view={view}
      onViewChange={handleViewChange}
      objectives={objectives}
      ladderMap={ladderMap}
      isLoading={!currentUser || isLoading}
      error={error ? { message: error.message } : null}
      nextCursor={nextCursor}
      onLoadMore={nextCursor ? () => setCursor(nextCursor) : undefined}
      emptyTitle="No objectives found"
      emptyDescription="Create your first objective to get started, or adjust your filters."
      emptyAction={<CreateObjectiveDialog />}
    />
  );
}
